import type {
  InitGameOptions,
  KingsCrownPlayer,
  KingsCrownState,
} from "./types";
import {
  KINGS_CROWN_INITIAL_HAND_SIZE,
  KINGS_CROWN_MAX_HAND_SIZE,
} from "./types";
import {
  createBoard,
  createChipBag,
  createTableSlots,
  getDefaultSeed,
  shuffle,
} from "./utils";

export function initGame(options: InitGameOptions): KingsCrownState {
  const playerIds = [...new Set(options.playerIds)];
  if (playerIds.length !== 2) {
    throw new Error("Kings Crown requires exactly two players");
  }

  const seed = options.seed ?? getDefaultSeed(playerIds);
  const firstPlayerId =
    options.firstPlayerId ?? playerIds[seed % playerIds.length];
  if (!firstPlayerId || !playerIds.includes(firstPlayerId)) {
    throw new Error("First player must be one of the players");
  }

  const shuffledBag = shuffle(createChipBag(), seed);
  const players: readonly KingsCrownPlayer[] = playerIds.map(
    (playerId, index) => {
      const start = index * KINGS_CROWN_INITIAL_HAND_SIZE;
      const hand = shuffledBag.slice(
        start,
        start + KINGS_CROWN_INITIAL_HAND_SIZE,
      );
      return {
        playerId,
        hand,
        handSlots: Array.from(
          { length: KINGS_CROWN_MAX_HAND_SIZE },
          (_, slotIndex) => hand[slotIndex] ?? null,
        ),
        remainingCrowns: KINGS_CROWN_MAX_HAND_SIZE,
      };
    },
  );

  return {
    gameId: options.gameId ?? `kings-crown-${playerIds.join("-")}`,
    phase: "DRAFT_DRAW",
    playerOrder: playerIds,
    currentPlayerId: firstPlayerId,
    firstPlayerId,
    players,
    bag: shuffledBag.slice(playerIds.length * KINGS_CROWN_INITIAL_HAND_SIZE),
    board: createBoard(),
    table: createTableSlots(),
    drawnSlotIds: [],
    completedActionIds: [],
    lastPlacementPlayerId: null,
    winnerId: null,
    winLine: [],
    seed,
  };
}
